<template>
  <nav class="barra" :aria-label="$t('footer.menu')">
    <ul class="barra__lista">
      <li
        v-for="item in barra"
        :key="item.id"
        class="barra__item"
        :class="{ 'barra__item--ativo': ativo(item) }"
      >
        <component
          :is="item.acao ? 'button' : 'router-link'"
          :to="item.acao ? null : item.rota"
          :type="item.acao ? 'button' : null"
          class="barra__destino"
          :class="[`barra__destino--${item.id}`, { 'barra__destino--ativo': ativo(item) }]"
          :aria-current="ativo(item) ? 'page' : null"
          :aria-label="item.rotulo"
          @click="item.acao && item.acao()"
          @pointerdown="comecarToqueLongo(item)"
          @pointerup="cancelarToqueLongo"
          @pointerleave="cancelarToqueLongo"
          @pointercancel="cancelarToqueLongo"
        >
          <FishIcon :nome="item.icone" tamanho="grande" class="barra__icone" />

          <!--
            O RÓTULO SÓ NO ATIVO (D5).

            "Fora os rótulos, ícones grandes. O jogo comunica por ícone." Mas tirar o rótulo tira uma
            migalha, e o D5 pede duas compensações: o rótulo no ativo (aqui) e o toque longo
            (abaixo). O nome continua no `aria-label` para quem não vê o ícone.
          -->
          <span v-if="ativo(item)" class="barra__rotulo">{{ item.rotulo }}</span>

          <!--
            O brilho suave quando há novidade (3.4, D0).

            "Brilho suave no 'Mais' — nunca badge vermelho." A roleta "deve se anunciar quando há
            ticket, não ocupar espaço permanente".
          -->
          <span v-if="item.brilha" class="barra__brilho repouso-brilha" aria-hidden="true" />
        </component>

        <!-- O tooltip do toque longo (3.5). No desktop ele também aparece no hover. -->
        <span v-if="tooltip === item.id" class="barra__tooltip" role="tooltip">{{ item.dica }}</span>
      </li>
    </ul>

    <!-- A FOLHA "MAIS" (3.3) — ela substitui a `mobile-sidebar`, que foi apagada. -->
    <Teleport to="body">
      <Transition name="folha">
        <div v-if="maisAberto" class="mais" @click.self="fecharMais">
          <div class="mais__folha" role="dialog" aria-modal="true" :aria-label="$t('footer.menu')">
            <header class="mais__topo">
              <h2 class="t-titulo">{{ $t('footer.menu') }}</h2>
              <button class="mais__fechar" type="button" :aria-label="$t('footer.close') || 'Fechar'" @click="fecharMais">
                <FishIcon nome="times" tamanho="pequeno" />
              </button>
            </header>

            <ul class="mais__lista">
              <li v-for="item in noMais" :key="item.id">
                <router-link class="mais__destino" :to="item.rota" @click="fecharMais">
                  <FishIcon :nome="item.icone" tamanho="medio" />
                  <span class="mais__rotulo">{{ item.rotulo }}</span>
                  <small class="mais__dica t-legenda">{{ item.dica }}</small>
                  <span v-if="item.brilha" class="barra__brilho repouso-brilha" aria-hidden="true" />
                </router-link>
              </li>
              <li>
                <button class="mais__destino" type="button" @click="abrirConfiguracoes">
                  <FishIcon nome="cog" tamanho="medio" />
                  <span class="mais__rotulo">{{ $t('configs.title') || 'Configurações' }}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- A dica de primeira sessão (3.5): uma vez só, e some ao primeiro toque. -->
    <Transition name="folha">
      <p v-if="mostrarDicaInicial" class="barra__dica-inicial" role="status">
        {{ $t('footer.firstHint') || 'Segure um ícone para ver o nome.' }}
      </p>
    </Transition>

    <BuyParalleliums v-if="moedasAberto" @close="moedasAberto = false" />
  </nav>
</template>

<script>
import BuyParalleliums from '@/shared/modals/BuyParalleliums.vue';
import FishIcon from '@/shared/ui/FishIcon.vue';
import { travarRolagem, destravarRolagem } from '@/shared/ui/rolagem';

const CHAVE_DA_DICA = 'fishbay:dica-navegacao';
const ESPERA_DO_TOQUE_LONGO = 450;

/**
 * 3.2 a 3.6 — a barra de navegação (D5, D12).
 *
 * ## O que havia
 *
 * `Footer.vue` montava **dez destinos com ícone, rótulo e tooltip cada um**, e — na mesma tela —
 * uma `mobile-sidebar` com **os mesmos dez destinos**. Duas navegações completas para o mesmo lugar.
 * Com Press Start 2P nos rótulos (UI-01), os dez se amontoavam.
 *
 * ## A barra é o FLUXO, não uma lista de seções (D12)
 *
 * **Pescar · Loja · Inventário · Melhorias · Moedas**, mais o "Mais".
 *
 * É a ordem em que se joga: pesco, vendo e compro, vejo o que tenho, gasto o ouro em atributo, e
 * compro parallelium quando quero acelerar. *"A barra é a sequência da sessão, não uma lista de
 * seções."*
 *
 * ## Por que cada um dos outros saiu
 *
 * | Item | Por que sai |
 * |---|---|
 * | **Mapas** | escolher onde pescar acontece **dentro de Pescar** (D21, 7.5c), não como destino |
 * | **Casa** | só existe se você comprou uma — era `v-if="hasHouse"`. A maioria não tem |
 * | **Negócios** | sistema passivo, e o armazém pede nível 5. Confere-se de vez em quando |
 * | **Clã** | pede nível 10 e 50.000 de ouro. Social, visita ocasional |
 * | **Roleta** | depende de ticket. Deve **se anunciar** quando há um, não ocupar espaço permanente |
 * | **Início** | virou o Cais, e **o emblema no topo leva para lá** (3.6) — convenção conhecida |
 *
 * ## A correção que aparece de graça (D12)
 *
 * Início e Casa usavam **o mesmo `fa-home`**. Dois destinos com o mesmo desenho é a tela dizendo que
 * são a mesma coisa — e com navegação só por ícone isso deixaria de ser deselegante e passaria a
 * ser quebrado. Início saiu da barra e Casa foi para o "Mais", com ícone próprio.
 *
 * ## Uma nota sobre o D12
 *
 * O critério foi **posição no laço de jogo**, não medição: o gtag está configurado com
 * `send_page_view: false`, então não há dado de uso. **Vale revisar quando houver número.**
 */
export default {
  name: 'GameFooter',
  components: { BuyParalleliums, FishIcon },
  emits: ['open-configs'],
  data() {
    return {
      moedasAberto: false,
      maisAberto: false,
      tooltip: null,
      mostrarDicaInicial: false,
      relogioDoToque: null,
    };
  },
  computed: {
    temCasa() {
      return this.$store.state.player?.inventory?.houses?.length > 0;
    },
    /** Há ticket de roleta? É o que faz o "Mais" brilhar (3.4). */
    temTicket() {
      return Number(this.$store.state.player?.rouletteTickets || 0) > 0;
    },
    /** Os cinco do fluxo, mais o "Mais" (D12). */
    barra() {
      return [
        { id: 'pescar', rota: '/fishing', icone: 'nav-pescar', rotulo: this.$t('footer.fish'), dica: this.$t('footer.fishTooltip') },
        { id: 'loja', rota: '/shop', icone: 'nav-loja', rotulo: this.$t('footer.shop'), dica: this.$t('footer.shopTooltip') },
        { id: 'inventario', rota: '/inventory', icone: 'nav-inventario', rotulo: this.$t('footer.player'), dica: this.$t('footer.playerTooltip') },
        { id: 'melhorias', rota: '/upgrade', icone: 'nav-melhorias', rotulo: this.$t('footer.upgrade'), dica: this.$t('footer.upgradeTooltip') },
        {
          id: 'moedas',
          icone: 'nav-moedas',
          rotulo: this.$t('footer.paralleliums'),
          dica: this.$t('footer.paralleliumsTooltip'),
          acao: () => { this.moedasAberto = true; },
        },
        {
          id: 'mais',
          icone: 'nav-mais',
          rotulo: this.$t('footer.menu'),
          dica: this.$t('footer.menu'),
          brilha: this.temTicket,
          acao: this.abrirMais,
        },
      ];
    },
    /** Os oito restantes. `Casa` só aparece para quem tem uma — a regra do `v-if` antigo continua. */
    noMais() {
      const itens = [
        { id: 'inicio', rota: '/home', icone: 'home', rotulo: this.$t('footer.home'), dica: this.$t('footer.homeTooltip') },
        { id: 'mapas', rota: '/maps', icone: 'map', rotulo: this.$t('footer.maps'), dica: this.$t('footer.mapsTooltip') },
        { id: 'negocios', rota: '/business', icone: 'briefcase', rotulo: this.$t('footer.business'), dica: this.$t('footer.businessTooltip') },
        { id: 'clan', rota: '/clan', icone: 'users', rotulo: this.$t('footer.clan'), dica: this.$t('footer.clanTooltip') },
        // A 1.2 entra no menu Mais, ao lado de Negócios: é o segundo loop do
        // jogo, e não uma das seis coisas que se faz a toda hora.
        { id: 'expedicoes', rota: '/exploration', icone: 'map', rotulo: this.$t('exploration.title'), dica: this.$t('exploration.subtitle') },
        // O mercado entre jogadores entra aqui, e não na barra de seis: ele é
        // ocasional (vende-se o que sobrou, não a cada pescaria) e depende de ter
        // sobrado algo. `handshake` e não `store-alt` — a loja do NPC já é
        // `nav-loja`, e dois destinos com o mesmo desenho é o defeito do D12.
        { id: 'mercado', rota: '/market', icone: 'handshake', rotulo: this.$t('market.title'), dica: this.$t('market.subtitle') },
        { id: 'roleta', rota: '/roulette', icone: 'dice', rotulo: this.$t('footer.roulette'), dica: this.$t('footer.rouletteTooltip'), brilha: this.temTicket },
      ];

      // `door-open` e não `home`: o `fa-home` já é o Início, e dois destinos com o mesmo desenho é
      // exatamente o defeito que o D12 aponta.
      if (this.temCasa) {
        itens.push({ id: 'casa', rota: '/house-rest', icone: 'door-open', rotulo: this.$t('footer.house'), dica: this.$t('footer.houseTooltip') });
      }

      itens.push({ id: 'perfil', rota: '/inventory', icone: 'user', rotulo: this.$t('profile.title') || 'Perfil', dica: '' });
      return itens;
    },
  },
  mounted() {
    try {
      // A dica de primeira sessão: **uma vez só** (D5). Ela existe porque tirar o rótulo tira uma
      // migalha, e alguém tem que dizer ao jogador que o nome ainda está lá.
      if (!localStorage.getItem(CHAVE_DA_DICA)) {
        this.mostrarDicaInicial = true;
        localStorage.setItem(CHAVE_DA_DICA, '1');
        setTimeout(() => { this.mostrarDicaInicial = false; }, 6000);
      }
    } catch {
      /* sem armazenamento: a dica não aparece, e tudo bem */
    }
  },
  beforeUnmount() {
    this.cancelarToqueLongo();
    if (this.maisAberto) destravarRolagem();
  },
  methods: {
    ativo(item) {
      return !item.acao && this.$route.path === item.rota;
    },
    abrirMais() {
      this.maisAberto = true;
      travarRolagem();
      this.mostrarDicaInicial = false;
    },
    fecharMais() {
      this.maisAberto = false;
      destravarRolagem();
    },
    abrirConfiguracoes() {
      this.fecharMais();
      this.$emit('open-configs');
    },
    /**
     * O toque longo mostra o nome (3.5, D5).
     *
     * `pointerdown` e não `touchstart`: o mesmo caminho serve a mouse, dedo e caneta, e evita ter
     * dois conjuntos de ouvintes que podem discordar. O tempo é maior que o do arrasto (450ms
     * contra 220ms) porque aqui não há nada a levantar — o custo de esperar é só esperar.
     */
    comecarToqueLongo(item) {
      this.cancelarToqueLongo();
      this.relogioDoToque = setTimeout(() => { this.tooltip = item.id; }, ESPERA_DO_TOQUE_LONGO);
    },
    cancelarToqueLongo() {
      if (this.relogioDoToque) clearTimeout(this.relogioDoToque);
      this.relogioDoToque = null;
      // O tooltip some um pouco depois do dedo sair: some no mesmo quadro e ninguém consegue ler.
      if (this.tooltip) setTimeout(() => { this.tooltip = null; }, 900);
    },
  },
};
</script>

<style scoped>
/**
 * A barra são SEIS FOLHAS DE PAPEL RASGADO, e não uma faixa com células dentro.
 *
 * ## O que havia, e por que as duas versões anteriores falharam
 *
 * A primeira era uma viga de madeira com seis ícones a 72% de opacidade flutuando nela. O
 * diagnóstico do autor: *"parecem botões; o nosso não parece que são botões"*. Estava certo — o
 * destino inativo era `border: 2px solid transparent` e mais nada. Não havia objeto: havia ícone
 * apagado sobre textura, e nada com forma de alvo.
 *
 * A segunda tentou consertar dando corpo aos destinos com o `encaixe.png`. Esse sprite **é azulado**
 * (ele é o buraco do inventário, feito para o `--fundo-encaixe`, que é `#071a26`), e sobre madeira
 * virou retângulo com contorno azul — pior que o problema. Foi usado sem ser olhado a 6×, que é a
 * regra que o `src/assets/ui/README.md` manda seguir justamente para isso não acontecer.
 *
 * ## A saída, e por que ela é do jogo e não uma ideia solta
 *
 * O autor propôs papel: *"como se fosse pedaços de papel rasgado em cada botão... a textura de papel
 * pode ficar mais legal, até mesmo que essa de madeira que tentou imitar e não ficou bom"*.
 *
 * O Inventário já é o Caderno do Pescador (D17), os Mapas são carta náutica (D21), o CatchResult é
 * papel, a Taxa do porto é recibo. **A barra era o último lugar do jogo insistindo em madeira.**
 *
 * ## O que faz seis papéis lerem como seis botões
 *
 * 1. **Cada folha tem silhueta PRÓPRIA.** São seis variantes diferentes do Paper Pack, fixas por
 *    destino (`recortar-papel.mjs`). Com o mesmo sprite seis vezes a barra volta a ser uma grade de
 *    células idênticas — o defeito original com outra textura.
 * 2. **Há vão entre elas.** Encostadas, as seis se fundem numa folha grande só; foi o primeiro
 *    defeito que a montagem em PNG mostrou.
 * 3. **O rasgo aponta para cima**, então a silhueta do topo da barra é irregular: nenhuma linha reta
 *    atravessa a tela. É por aí que a barra "sai" de ser um retângulo.
 * 4. **Não há viga.** O que segura as folhas é a opacidade delas; atrás fica a cena do jogo.
 */
.barra {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-navegacao);

  /**
   * Fundo NENHUM — nem viga, nem borda de latão, nem véu.
   *
   * Houve um véu escuro aqui por um momento, e o desktop o reprovou na primeira foto: a lista tem
   * `max-width`, então numa tela de 1440px o véu virava uma tarja preta atravessando a tela inteira
   * com seis folhinhas perdidas no meio. O papel é opaco e se sustenta sozinho; quem dá pé às
   * folhas é a sombra de cada uma, não uma faixa atrás de todas.
   */
  background: none;
  /* A área segura do aparelho: sem isto a barra fica atrás da barra de gestos do iPhone e o último
   * ícone vira inalcançável. */
  padding-bottom: env(safe-area-inset-bottom, 0px);
  /**
   * A caixa é TRANSPARENTE e não pode ser alvo. Ela ocupa a largura toda na altura da folha ativa
   * (99px), e engolia o toque em tudo o que a página tivesse nessa faixa — nos vãos entre as folhas
   * e acima das folhas baixas. Foi o "a hitbox da seta não está na seta" da enciclopédia (relato de
   * 23/09): a seta de próxima página caía dentro da caixa, e só o pedaço acima dela respondia. Só
   * o papel recebe toque (`.barra__destino`).
   */
  pointer-events: none;
}

.barra__lista {
  display: flex;
  /* Centradas, e cada uma com o tamanho da própria arte. A folha não estica mais para preencher a
   * linha — o 9-slice foi embora (ver o comentário do `.barra__destino`), então a largura é a do
   * sprite em escala inteira, e o que sobra na linha vira vão. */
  justify-content: center;
  align-items: flex-end;
  gap: 8px;
  padding: var(--e-1) var(--e-1) 0;
  /* As folhas SAEM DE BAIXO DA TELA: a lista desce 4px além da borda, então nenhuma página mostra o
   * contorno de baixo — todas estão presas na aresta, não pousadas em cima de uma margem. Havia 4px
   * de recuo aqui e o autor viu na hora: "deveria encostar na parte de baixo, ficou feio". */
  margin-bottom: -4px;
}

.barra__item {
  position: relative;
  flex: none;
  display: flex;
}

.barra__destino {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  /**
   * A FOLHA INTEIRA, em escala inteira — 27×33 desenhados, 54×66 na tela.
   *
   * Houve uma versão em 9-slice com rotação por CSS, e o autor a reprovou: "está meio bugado". Era
   * renderização, não arte: girar bitmap pixelado 2° interpola o contorno de 1px numa mancha de
   * 2px, o `round` do 9-slice repetia o picote em linha tracejada, e a folha ativa esticava para
   * caber o rótulo. Nada disso existe quando a página vai inteira, como desenhada, em 2× exato:
   * cada pixel do sprite vira um quadrado de 2×2 e mais nada.
   *
   * O preço é largura fixa. O rótulo do destino atual, que não cabe em 54px, foi para a faixa por
   * cima da folha (`.barra__rotulo`). Abaixo de ~330px de tela as seis não cabem lado a lado e a
   * linha transborda alguns pixels — aceito; é o iPhone SE de primeira geração.
   */
  width: 54px;
  height: 66px;
  /* A `.barra` não recebe toque (ver lá); o papel, sim. */
  pointer-events: auto;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  image-rendering: pixelated;

  color: var(--tinta-fraca);
  text-decoration: none;

  /**
   * CADA FOLHA POUSA NUMA ALTURA — é daqui que vem a irregularidade "de jogo" que o autor pediu.
   *
   * Ele pediu rotação. Testou-se de duas formas: por CSS (borra — ver acima) e pré-girada no sprite
   * com vizinho mais próximo (a 27px de largura, 2° não cabe num degrau de pixel: o contorno quebra
   * e abre buracos). Pixel art não inclina nesse tamanho. Deslocar na vertical é inteiro, não
   * borra, e produz o mesmo efeito de papel largado sem alinhar: seis folhas em seis alturas.
   *
   * Fixo por destino, não sorteado: uma barra que muda de forma a cada abertura desorienta.
   */
  --pouso: 0px;
  transform: translateY(var(--pouso));
  transition: transform var(--t-rapido) var(--curva-quique),
    filter var(--t-rapido) var(--curva-suave);
}

/**
 * A silhueta e o pouso de cada destino, um por um.
 *
 * Explícito e não `:nth-child`: a folha pertence ao DESTINO, não à posição. Se a ordem da barra
 * mudar (o D12 já avisa que vale revisar quando houver número de uso), o jogador continua
 * reconhecendo o lugar pelo formato.
 */
.barra__destino--pescar { --pouso: 2px; background-image: url('/ui/folha-pescar.png'); }
.barra__destino--loja { --pouso: 0px; background-image: url('/ui/folha-loja.png'); }
.barra__destino--inventario { --pouso: 3px; background-image: url('/ui/folha-inventario.png'); }
.barra__destino--melhorias { --pouso: 1px; background-image: url('/ui/folha-melhorias.png'); }
.barra__destino--moedas { --pouso: 3px; background-image: url('/ui/folha-moedas.png'); }
.barra__destino--mais { --pouso: 0px; background-image: url('/ui/folha-mais.png'); }

.barra__destino--pescar.barra__destino--ativo { background-image: url('/ui/folha-pescar-aceso.png'); }
.barra__destino--loja.barra__destino--ativo { background-image: url('/ui/folha-loja-aceso.png'); }
.barra__destino--inventario.barra__destino--ativo { background-image: url('/ui/folha-inventario-aceso.png'); }
.barra__destino--melhorias.barra__destino--ativo { background-image: url('/ui/folha-melhorias-aceso.png'); }
.barra__destino--moedas.barra__destino--ativo { background-image: url('/ui/folha-moedas-aceso.png'); }
.barra__destino--mais.barra__destino--ativo { background-image: url('/ui/folha-mais-aceso.png'); }

.barra__icone {
  /* 32px é o TAMANHO NATIVO do sprite (medido: os seis `nav-*.png` são 32×32). Os 40px de antes eram
   * 1,25× — escala fracionária em pixel art, que borra cada traço. */
  width: 32px;
  height: 32px;
  opacity: 1;
}

/**
 * O ATIVO: a mesma folha, em papel mais claro, LEVANTADA — e reta, sem pouso.
 *
 * A distinção é de MATERIAL, não de cor de realce: papel-luz contra papel comum. É a convenção que o
 * `FishTabs` já usa (*"a aba ativa vira pergaminho — o mesmo material da página que ela abre"*), e
 * ela sobrevive a daltonismo, que uma tarja dourada não faria. Sem sombra, por veto do autor ("sem
 * 3D"): claro, alto e reto já diz "esta".
 */
.barra__destino--ativo {
  color: var(--tinta);
  /**
   * A ativa é MAIOR, não mais alta: 3× em vez de 2× (81×99), com o pé no mesmo lugar das outras.
   *
   * Antes ela subia 14px, e o autor viu o defeito na animação: "sobe demais, sendo que poderia só
   * aumentar o scale". Crescer por múltiplo inteiro do sprite (27×33 → ×3) continua pixel-perfeito
   * em repouso; é o `scale()` fracionário permanente que borraria. A animação de chegada usa
   * `scale` só DURANTE o movimento e termina em identidade — nítido onde importa, que é parado.
   */
  width: 81px;
  height: 99px;
  transform: none;
  transform-origin: 50% 100%;
  z-index: 1;
  animation: folha-puxada var(--t-medio) var(--curva-quique);
}

.barra__destino--ativo .barra__icone {
  width: 48px;
  height: 48px;
}

@keyframes folha-puxada {
  0% {
    transform: scale(0.67); /* o tamanho de uma inativa: ela CRESCE a partir de onde estava */
  }
  70% {
    transform: scale(1.04);
  }
  100% {
    transform: scale(1);
  }
}

@media (hover: hover) {
  /* A folha se levanta um pouco antes de ser apertada — papel pegando ar. */
  .barra__destino:not(.barra__destino--ativo):hover {
    color: var(--tinta);
    transform: translateY(calc(var(--pouso) - 3px));
  }
}

/* Apertar AFUNDA a folha e a escurece, como papel prensado contra a mesa. `filter` e não troca de
 * sprite: não muda geometria, então continua nítido. */
.barra__destino:not(.barra__destino--ativo):active {
  filter: brightness(0.9);
  transform: translateY(calc(var(--pouso) + 2px));
}

/**
 * O RÓTULO É UMA FAIXA POR CIMA DA FOLHA — os Headers do Paper Pack, feitos para título.
 *
 * O nome não cabe numa página de 54px ("Inventário" precisa de ~65), e esticar a página era um dos
 * três defeitos que o autor viu. A faixa resolve pelo lado certo: é outro objeto, com as duas pontas
 * rasgadas intactas (`faixa-rotulo.png`, 21×16: dez colunas de cada ponta e uma lisa no meio) e só a
 * coluna lisa esticando pelo tamanho do nome. Fatia `0 10 0 10`, sem `fill`: o corpo é o token.
 *
 * Encosta 6px na folha para ler como presa nela, não flutuando.
 */
.barra__rotulo {
  position: absolute;
  bottom: calc(100% - 6px);
  left: 50%;
  transform: translateX(-50%);

  height: 32px;
  line-height: 32px;
  padding: 0 2px;
  border-style: solid;
  border-color: transparent;
  border-width: 0 20px 0 20px;
  border-image-source: url('/ui/faixa-rotulo.png');
  border-image-slice: 0 10 0 10;
  border-image-width: 0 20px 0 20px;
  border-image-repeat: stretch;
  background: var(--papel-luz);
  background-clip: padding-box;
  image-rendering: pixelated;

  font-family: var(--fonte-display);
  /* 12px, e não menos: a DotGothic16 é de grade 16 e abaixo de 12 os traços somem. */
  font-size: var(--f-1);
  color: var(--tinta);
  white-space: nowrap;
  animation: surgir var(--t-rapido) var(--curva-entrada) both;
}

/**
 * No PC tudo triplica — e é múltiplo INTEIRO, nunca 1,5×.
 *
 * Folha a 3× (81×99), faixa a 3×, ícone a 2× (64px). A barra fica alta e é deliberado — a memória do
 * redesenho registra que *"o autor acha meu padrão pequeno demais no PC — calibrar grande"*. Se
 * ficar exagerado, os números a mexer são estes e o `--altura-barra` do `App.vue`, que se mede por
 * eles.
 */
@media (min-width: 600px) {
  .barra__lista {
    gap: 12px;
  }

  .barra__destino {
    width: 81px;
    height: 99px;
  }

  /* A ativa a 4× (108×132); as outras a 3×. */
  .barra__destino--ativo {
    width: 108px;
    height: 132px;
  }

  /* 48 e não 64: com 64 o ícone encostava nas bordas da folha de 81 — "diminua um pouco os ícones",
   * pediu o autor. É 1,5× do sprite de 32, a única escala fracionária da barra, e a aceito porque a
   * alternativa (folha a 4× para caber o 2×) faria uma barra de 132px em toda tela de PC. Na
   * ativa, que é maior, o ícone volta ao 2× exato. */
  .barra__icone {
    width: 48px;
    height: 48px;
  }

  .barra__destino--ativo .barra__icone {
    width: 64px;
    height: 64px;
  }

  .barra__rotulo {
    height: 48px;
    line-height: 48px;
    border-width: 0 30px 0 30px;
    border-image-width: 0 30px 0 30px;
    font-size: var(--f-2);
  }
}

/**
 * O brilho: um ponto de latão que respira. Nunca vermelho, nunca com número (D0).
 *
 * Ele mudou de lugar e de cor. Em `right: 25%` boiava no meio do nada, porque não havia peça em que
 * pousar; agora mora na quina da folha, meio por cima da borda rasgada. E é `--ouro` e não
 * `--ouro-claro`: claro sobre papel claro sumia — a mesma armadilha que a versão de madeira nunca
 * teve, porque lá o fundo era escuro.
 */
.barra__brilho {
  position: absolute;
  top: -6px;
  right: -4px;
  width: 10px;
  height: 10px;
  border-radius: var(--r-circulo);
  background: var(--ouro);
  /* `outline` e não `box-shadow`: `repouso-brilha` anima `box-shadow`, e um aro escrito ali seria
   * apagado no primeiro quadro do repouso. */
  outline: 2px solid var(--tinta);
}

.barra__tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-balao);

  background: var(--papel-frio);
  color: var(--tinta);
  border: 2px solid var(--madeira-escura);
  padding: 2px var(--e-2);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  white-space: nowrap;
  animation: surgir var(--t-rapido) var(--curva-entrada) both;
  pointer-events: none;
}

.barra__dica-inicial {
  position: absolute;
  bottom: calc(100% + var(--e-2));
  left: var(--e-4);
  right: var(--e-4);
  background: var(--madeira);
  border: 2px solid var(--latao);
  color: var(--texto);
  font-size: var(--f-2);
  padding: var(--e-2);
  text-align: center;
}

/* --- A folha "Mais" (3.3) --------------------------------------------------------------------- */

.mais {
  position: fixed;
  inset: 0;
  z-index: var(--z-folha);
  display: flex;
  align-items: flex-end;
  background: var(--veu);
}

.mais__folha {
  width: 100%;
  max-height: 80vh;
  max-height: 80dvh;
  overflow-y: auto;
  background: var(--fundo-painel);
  border-top: 3px solid var(--latao);
  padding: var(--e-4);
  padding-bottom: calc(var(--e-4) + env(safe-area-inset-bottom, 0px));
  /* O painel é PAPEL — o texto é TINTA. A família `--texto*` é para fundo escuro; usá-la aqui
   * deixava o menu quase ilegível (veto do autor, 03/09: "tá claro demais a fonte"). */
  color: var(--tinta);
}

@media (min-width: 900px) {
  .mais {
    align-items: center;
    justify-content: center;
  }

  .mais__folha {
    max-width: 480px;
    border: 3px solid var(--latao);
  }
}

.mais__topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--e-4);
}

.mais__fechar {
  color: var(--tinta-fraca);
}

.mais__lista {
  display: grid;
  gap: var(--e-2);
}

.mais__destino {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--e-3);
  width: 100%;
  min-height: 56px;
  padding: var(--e-2) var(--e-3);
  text-align: left;

  border: 2px solid var(--madeira);
  background: var(--fundo-elevado);
  color: var(--tinta);
  text-decoration: none;
  transition: border-color var(--t-rapido) var(--curva-suave), transform var(--t-rapido) var(--curva-quique);
}

.mais__destino:active {
  transform: translateY(1px);
}

@media (hover: hover) {
  .mais__destino:hover {
    border-color: var(--latao);
  }
}

.mais__rotulo {
  font-family: var(--fonte-display);
  font-size: var(--f-3);
}

.mais__dica {
  margin-left: auto;
  text-align: right;
  max-width: 45%;
  /* O `.t-legenda` vem com `--texto-fraco` (família de fundo escuro); sobre papel é tinta. */
  color: var(--tinta-fraca);
}

.folha-enter-active,
.folha-leave-active {
  transition: opacity var(--t-medio) var(--curva-suave);
}

.folha-enter-from,
.folha-leave-to {
  opacity: 0;
}

.folha-enter-active .mais__folha {
  animation: folha-sobe var(--t-medio) var(--curva-entrada) both;
}

.folha-leave-active .mais__folha {
  animation: folha-desce var(--t-medio) var(--curva-saida) both;
}
</style>
